import React, { useState, useEffect } from 'react';
import Calling from '../../api/Calling';
import Loader from '../../components/Loader';
import { FiPhone, FiPhoneOff, FiPhoneMissed, FiRefreshCw, FiMessageCircle, FiChevronDown, FiSave, FiClock, FiX, FiBox } from 'react-icons/fi';
import { IoLogoWhatsapp } from 'react-icons/io5';
import { FaTelegramPlane } from 'react-icons/fa';
import { toast } from 'react-toastify';
import formatDate from '../../components/formatDate';

const ShowBox = () => {
    const [loader, setLoader] = useState(true);
    const [showboxData, setShowboxData] = useState(null);
    const [originalData, setOriginalData] = useState(null);
    const [selectedType, setSelectedType] = useState('ringing');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    const showboxTypes = [
        { id: 'dailing', label: 'Dialing', icon: FiPhone, color: 'text-main-exBlue', bgColor: 'bg-main-exBlue/10' },
        { id: 'ringing', label: 'Ringing', icon: FiPhone, color: 'text-main-blue', bgColor: 'bg-main-th' },
        { id: 'misscall', label: 'Missed Call', icon: FiPhoneMissed, color: 'text-main-yellow', bgColor: 'bg-main-yellow/10' },
        { id: 'callend', label: 'Call Ended', icon: FiPhoneOff, color: 'text-main-delete', bgColor: 'bg-main-delete/10' },
    ];

    const topAdsOptions = [
        { value: 'NTV_SMALL_ADS', label: 'Native Small Ads (320 x 75)' },
        { value: 'BNR_INHOUSE', label: 'Banner Inhouse' },
        { value: 'OFF', label: 'Off' }
    ];

    const bottomAdsOptions = [
        { value: 'NTV_ADS', label: 'Native Ads' },
        { value: 'NTV_INHOUSE', label: 'Native Inhouse' },
        { value: 'OFF', label: 'Off' }
    ];

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (showboxData && originalData) {
            const changed = JSON.stringify(showboxData) !== JSON.stringify(originalData);
            setHasChanges(changed);
        }
    }, [showboxData, originalData]);

    const fetchData = async () => {
        setLoader(true);
        try {
            const resp = await Calling.getShowbox();
            if (resp.status && resp.data) {
                setShowboxData(resp.data);
                setOriginalData(resp.data);
                setLastUpdated(resp.data.updatedAt || resp.data.createdAt || null);
            } else {
                const defaultData = {
                    ringing_status: true,
                    misscall_status: true,
                    callend_status: true,
                    fake_status: true,
                    dailing_status: true,
                    ringing_top_Ads: 'BNR_SMALL_ADS',
                    ringing_bottom_Ads: 'NTV_ADS',
                    misscall_top_Ads: 'BNR_SMALL_ADS',
                    misscall_bottom_Ads: 'NTV_ADS',
                    callend_top_Ads: 'BNR_SMALL_ADS',
                    callend_bottom_Ads: 'NTV_ADS',
                    fake_top_Ads: 'BNR_SMALL_ADS',
                    fake_bottom_Ads: 'NTV_ADS',
                    dailing_top_Ads: 'BNR_SMALL_ADS',
                    dailing_bottom_Ads: 'NTV_ADS'
                };
                setShowboxData(defaultData);
                setOriginalData(defaultData);
                setLastUpdated(null);
            }
        } catch (error) {
            console.log(error);
            const defaultData = {
                ringing_status: true,
                misscall_status: true,
                callend_status: true,
                fake_status: true,
                ringing_top_Ads: 'BNR_SMALL_ADS',
                ringing_bottom_Ads: 'NTV_ADS',
                misscall_top_Ads: 'BNR_SMALL_ADS',
                misscall_bottom_Ads: 'NTV_ADS',
                callend_top_Ads: 'BNR_SMALL_ADS',
                callend_bottom_Ads: 'NTV_ADS',
                fake_top_Ads: 'BNR_SMALL_ADS',
                fake_bottom_Ads: 'NTV_ADS'
            };
            setShowboxData(defaultData);
            setOriginalData(defaultData);
            setLastUpdated(null);
        } finally {
            setLoader(false);
        }
    };

    const handleSave = async () => {
        if (!hasChanges) {
            toast.info('No changes to save', { autoClose: 1500 });
            return;
        }

        setSaving(true);
        try {
            const changedFields = [];

            Object.keys(showboxData).forEach(key => {
                if (showboxData[key] !== originalData[key] && !['_id', 'createdAt', 'updatedAt', '__v'].includes(key)) {
                    changedFields.push({ name: key, status: showboxData[key] });
                }
            });

            let allSuccess = true;
            for (const field of changedFields) {
                const resp = await Calling.updateShowbox(field);
                if (!resp.status) {
                    allSuccess = false;
                    toast.error(`Failed to update ${field.name}`);
                }
            }

            if (allSuccess) {
                toast.success('Settings saved successfully!', { autoClose: 1500 });
                setOriginalData({ ...showboxData });
                setLastUpdated(new Date().toISOString());
                setHasChanges(false);
            }
        } catch (error) {
            console.log(error);
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleDiscard = () => {
        if (originalData) {
            setShowboxData({ ...originalData });
            setHasChanges(false);
            toast.info('Changes discarded', { autoClose: 1500 });
        }
    };

    const handleStatusChange = (type) => {
        if (!showboxData) return;
        const fieldName = `${type}_status`;
        setShowboxData(prev => ({
            ...prev,
            [fieldName]: !prev[fieldName]
        }));
    };

    const handleAdsChange = (type, position, value) => {
        if (!showboxData) return;
        const fieldName = `${type}_${position}_Ads`;
        setShowboxData(prev => ({
            ...prev,
            [fieldName]: value
        }));
    };

    const getSelectedTypeInfo = () => {
        return showboxTypes.find(t => t.id === selectedType);
    };

    const getCurrentConfig = () => {
        if (!showboxData) return { status: false, topAds: 'OFF', bottomAds: 'OFF' };
        return {
            status: showboxData[`${selectedType}_status`] ?? false,
            topAds: showboxData[`${selectedType}_top_Ads`] ?? 'OFF',
            bottomAds: showboxData[`${selectedType}_bottom_Ads`] ?? 'OFF'
        };
    };

    if (loader || !showboxData) return <Loader />;

    const typeInfo = getSelectedTypeInfo();
    const currentConfig = getCurrentConfig();

    return (
        <div className="w-full min-h-screen bg-main-bg overflow-hidden rounded-2xl">
            <div className="p-4">
                {/* Header */}
                <div className="bg-main-white rounded-2xl border border-main-border shadow-sm p-5 md:p-6 mb-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-main-th rounded-xl flex items-center justify-center border border-main-blue/20">
                                <FiBox className="text-main-blue text-xl" />
                            </div>
                            <div>
                                <h1 className="text-xl md:text-2xl font-bold text-main-font">ShowBox Settings</h1>
                                <p className="text-main-subfont text-sm mt-0.5">Configure popup display settings for calls</p>
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                            {/* Last Updated Time */}
                            <div className="hidden sm:flex items-center gap-2 text-xs text-main-subfont bg-main-bg px-3 py-2.5 rounded-xl border border-main-border">
                                <FiClock size={14} />
                                <span>Last saved: {formatDate(lastUpdated)}</span>
                            </div>

                            {/* Unsaved Changes Indicator */}
                            {hasChanges && (
                                <span className="text-xs bg-main-yellow/15 text-main-yellow px-3 py-2 rounded-xl font-semibold animate-pulse border border-main-yellow/30">
                                    Unsaved Changes
                                </span>
                            )}

                            <button
                                onClick={fetchData}
                                className="flex items-center gap-2 px-4 py-2.5 bg-main-white border border-main-border rounded-xl hover:bg-main-bg text-main-font transition-all font-medium text-sm"
                            >
                                <FiRefreshCw size={16} />
                                <span className="hidden sm:inline">Refresh</span>
                            </button>

                            {/* Discard Button */}
                            {hasChanges && (
                                <button
                                    onClick={handleDiscard}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-main-white border border-main-delete text-main-delete rounded-xl hover:bg-main-delete/10 transition-all font-medium text-sm"
                                >
                                    <FiX size={16} />
                                    <span className="hidden sm:inline">Discard</span>
                                </button>
                            )}

                            {/* Save Button */}
                            <button
                                onClick={handleSave}
                                disabled={saving || !hasChanges}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl transition-all font-semibold text-sm ${hasChanges
                                    ? 'bg-main-blue text-main-white hover:bg-[#2db84d] shadow-md shadow-main-blue/25'
                                    : 'bg-main-border text-main-subfont cursor-not-allowed'
                                    }`}
                            >
                                {saving ? (
                                    <div className="w-4 h-4 border-2 border-main-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <FiSave size={16} />
                                )}
                                <span className="hidden sm:inline">Save Changes</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Configuration Panel */}
                    <div className="bg-main-white rounded-2xl border border-main-border shadow-sm p-5 md:p-6">
                        <h2 className="text-lg font-bold text-main-font mb-5">Configuration</h2>

                        {/* Type Selector Dropdown */}
                        <div className="mb-6">
                            <label className="block text-sm font-semibold text-main-font mb-2">ShowBox Type</label>
                            <div className="relative">
                                <button
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    className="w-full flex items-center justify-between px-4 py-3.5 bg-main-th border border-main-blue/20 rounded-xl hover:bg-main-thHover transition-all"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`p-2.5 rounded-lg ${typeInfo.bgColor} border border-current/10`}>
                                            <typeInfo.icon className={typeInfo.color} size={20} />
                                        </div>
                                        <span className="font-semibold text-main-font">{typeInfo.label}</span>
                                    </div>
                                    <FiChevronDown className={`text-main-subfont transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} size={20} />
                                </button>

                                {isDropdownOpen && (
                                    <div className="absolute top-full left-0 right-0 mt-2 bg-main-white border border-main-border rounded-xl shadow-lg z-10 overflow-hidden">
                                        {showboxTypes.map((type) => (
                                            <button
                                                key={type.id}
                                                onClick={() => {
                                                    setSelectedType(type.id);
                                                    setIsDropdownOpen(false);
                                                }}
                                                className={`w-full flex items-center gap-3 px-4 py-3.5 hover:bg-main-th transition-all ${selectedType === type.id ? 'bg-main-th' : ''}`}
                                            >
                                                <div className={`p-2 rounded-lg ${type.bgColor}`}>
                                                    <type.icon className={type.color} size={20} />
                                                </div>
                                                <span className="font-medium text-main-font">{type.label}</span>
                                                {selectedType === type.id && (
                                                    <div className="ml-auto w-2.5 h-2.5 rounded-full bg-main-blue" />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Status Toggle */}
                        <div className="mb-6 p-4 bg-main-bg rounded-xl border border-main-border">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="font-semibold text-main-font">{typeInfo.label} Status</h3>
                                    <p className="text-sm text-main-subfont mt-0.5">Enable or disable this showbox type</p>
                                </div>
                                <button
                                    onClick={() => handleStatusChange(selectedType)}
                                    className={`relative w-14 h-7 rounded-full transition-all ${currentConfig?.status ? 'bg-main-blue' : 'bg-main-border'}`}
                                >
                                    <div className={`absolute top-1 w-5 h-5 bg-main-white rounded-full shadow-md transition-all ${currentConfig?.status ? 'left-8' : 'left-1'}`} />
                                </button>
                            </div>
                        </div>

                        {/* Top Ads Selection */}
                        <div className="mb-6">
                            <label className="block text-sm font-semibold text-main-font mb-2">Top Ads Type</label>
                            <select
                                value={currentConfig?.topAds || 'OFF'}
                                onChange={(e) => handleAdsChange(selectedType, 'top', e.target.value)}
                                className="w-full px-4 py-3.5 bg-main-white border border-main-border rounded-xl focus:outline-none focus:ring-2 focus:ring-main-blue/20 focus:border-main-blue text-main-font transition-all"
                            >
                                {topAdsOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Bottom Ads Selection */}
                        <div className="mb-6">
                            <label className="block text-sm font-semibold text-main-font mb-2">Bottom Ads Type</label>
                            <select
                                value={currentConfig?.bottomAds || 'OFF'}
                                onChange={(e) => handleAdsChange(selectedType, 'bottom', e.target.value)}
                                className="w-full px-4 py-3.5 bg-main-white border border-main-border rounded-xl focus:outline-none focus:ring-2 focus:ring-main-blue/20 focus:border-main-blue text-main-font transition-all"
                            >
                                {bottomAdsOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* All Types Quick Status */}
                        <div className="border-t border-main-border pt-5 mt-6">
                            <h3 className="text-sm font-semibold text-main-font mb-4">Quick Status Overview</h3>
                            <div className="grid grid-cols-2 gap-3">
                                {showboxTypes.map((type) => (
                                    <div
                                        key={type.id}
                                        onClick={() => setSelectedType(type.id)}
                                        className={`p-3.5 rounded-xl border cursor-pointer transition-all ${selectedType === type.id ? 'border-main-blue bg-main-th' : 'border-main-border hover:bg-main-bg hover:border-main-blue/30'}`}
                                    >
                                        <div className="flex items-center gap-2 mb-2.5">
                                            <type.icon className={type.color} size={16} />
                                            <span className="text-xs font-semibold text-main-font">{type.label}</span>
                                        </div>
                                        <div className={`text-xs px-2.5 py-1.5 rounded-lg text-center font-semibold ${showboxData?.[`${type.id}_status`] ? 'bg-main-th text-main-blue border border-main-blue/20' : 'bg-main-delete/10 text-main-delete border border-main-delete/20'}`}>
                                            {showboxData?.[`${type.id}_status`] ? 'Active' : 'Inactive'}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Preview Panel */}
                    <div className="bg-main-white rounded-2xl border border-main-border shadow-sm p-5 md:p-6">
                        <h2 className="text-lg font-bold text-main-font mb-2">Live Preview</h2>
                        <p className="text-sm text-main-subfont mb-5">This is how the popup will appear on the app</p>

                        {/* Phone Frame */}
                        <div className="flex justify-center">
                            <div className="w-72 bg-main-font rounded-3xl p-3 shadow-2xl">
                                <div className="bg-[#1f2937] rounded-2xl overflow-hidden">
                                    {/* Status Bar */}
                                    <div className="flex items-center justify-between px-4 py-2 text-main-white text-xs">
                                        <span>9:41</span>
                                        <div className="flex items-center gap-1">
                                            <div className="w-4 h-2 border border-main-white rounded-sm">
                                                <div className="w-2 h-full bg-main-white rounded-sm" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* ShowBox Content */}
                                    <div className="bg-main-white min-h-96">
                                        {/* Type Label */}
                                        <div className={`px-4 py-3 ${typeInfo.bgColor} border-b border-main-border`}>
                                            <div className="flex items-center gap-2">
                                                <typeInfo.icon className={typeInfo.color} size={18} />
                                                <span className={`font-semibold text-sm ${typeInfo.color}`}>{typeInfo.label}</span>
                                            </div>
                                        </div>

                                        {/* Top Ads Area */}
                                        {currentConfig?.topAds !== 'OFF' && (
                                            <div className="px-3 py-2">
                                                <div className={`rounded-lg overflow-hidden ${currentConfig?.topAds === 'BNR_BIG_ADS' ? 'h-20' : 'h-12'} bg-main-th flex items-center justify-center border border-dashed border-main-border`}>
                                                    <span className="text-xs text-main-subfont font-medium">
                                                        {topAdsOptions.find(o => o.value === currentConfig?.topAds)?.label}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Profile Section */}
                                        <div className="px-4 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-14 h-14 rounded-full bg-main-blue flex items-center justify-center text-main-white text-xl font-bold shadow-md">
                                                    D
                                                </div>
                                                <div className="flex-1">
                                                    <div className="text-xs text-main-subfont mb-0.5">domain.com</div>
                                                    <div className="font-semibold text-main-font">John Doe</div>
                                                    <div className="text-sm text-main-subfont">+91 98765 43210</div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Action Buttons - For Missed Call and Call Ended */}
                                        {(selectedType === 'misscall' || selectedType === 'callend') && (
                                            <div className="px-4 py-3">
                                                {selectedType === 'callend' && (
                                                    <div className="text-center py-2 mb-2 text-sm text-main-subfont">
                                                        Call Duration: 02:34
                                                    </div>
                                                )}

                                                <div className="flex items-center gap-2">
                                                    <button className="flex-1 flex flex-col items-center justify-center gap-1 py-3 bg-main-blue text-main-white rounded-xl font-medium text-xs shadow-md">
                                                        <IoLogoWhatsapp size={20} />
                                                        <span>WhatsApp</span>
                                                    </button>

                                                    <button className="flex-1 flex flex-col items-center justify-center gap-1 py-3 bg-main-exBlue text-main-white rounded-xl font-medium text-xs shadow-md">
                                                        <FiMessageCircle size={18} />
                                                        <span>Message</span>
                                                    </button>

                                                    <button className="flex-1 flex flex-col items-center justify-center gap-1 py-3 bg-[#0088cc] text-main-white rounded-xl font-medium text-xs shadow-md">
                                                        <FaTelegramPlane size={18} />
                                                        <span>Telegram</span>
                                                    </button>
                                                </div>

                                                {selectedType === 'misscall' && (
                                                    <button className="w-full flex items-center justify-center gap-2 py-3 mt-2 bg-main-yellow text-main-font rounded-xl font-medium text-sm shadow-md">
                                                        <FiPhone size={18} />
                                                        Call Back
                                                    </button>
                                                )}
                                            </div>
                                        )}

                                        {selectedType === 'ringing' && (
                                            <div className="px-4 py-6">
                                                <div className="flex flex-col items-center">
                                                    <div className="relative">
                                                        <div className="w-16 h-16 rounded-full bg-main-th flex items-center justify-center animate-pulse">
                                                            <FiPhone className="text-main-blue" size={28} />
                                                        </div>
                                                        <div className="absolute inset-0 w-16 h-16 rounded-full border-2 border-main-blue animate-ping opacity-50" />
                                                    </div>
                                                    <p className="mt-4 text-sm font-medium text-main-font">Incoming Call...</p>
                                                    <p className="text-xs text-main-subfont mt-1">Ringing</p>
                                                </div>
                                            </div>
                                        )}

                                        {selectedType === 'dailing' && (
                                            <div className="px-4 py-6">
                                                <div className="flex flex-col items-center">
                                                    <div className="relative">
                                                        <div className="w-16 h-16 rounded-full bg-main-exBlue/10 flex items-center justify-center">
                                                            <FiPhone className="text-main-exBlue" size={28} />
                                                        </div>
                                                        <div className="absolute inset-0 w-16 h-16 rounded-full border-2 border-main-exBlue animate-ping opacity-50" />
                                                    </div>
                                                    <p className="mt-4 text-sm font-medium text-main-font">Dialing...</p>
                                                    <p className="text-xs text-main-subfont mt-1">Connecting</p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Bottom Ads Area */}
                                        {currentConfig?.bottomAds !== 'OFF' && (
                                            <div className="px-3 py-2 pb-4">
                                                <div className={`rounded-lg overflow-hidden ${currentConfig?.bottomAds === 'BNR_BIG_ADS' ? 'h-20' : 'h-24'} bg-main-thHover flex items-center justify-center border border-dashed border-main-border`}>
                                                    <span className="text-xs text-main-subfont font-medium">
                                                        {bottomAdsOptions.find(o => o.value === currentConfig?.bottomAds)?.label}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Preview Info */}
                        <div className="mt-6 p-4 bg-main-th rounded-xl border border-main-blue/20">
                            <h4 className="font-semibold text-main-font text-sm mb-3">Current Configuration</h4>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between items-center py-1.5">
                                    <span className="text-main-subfont">Type:</span>
                                    <span className="font-semibold text-main-font">{typeInfo.label}</span>
                                </div>
                                <div className="flex justify-between items-center py-1.5">
                                    <span className="text-main-subfont">Status:</span>
                                    <span className={`font-semibold px-2.5 py-1 rounded-lg text-xs ${currentConfig?.status ? 'bg-main-blue/10 text-main-blue' : 'bg-main-delete/10 text-main-delete'}`}>
                                        {currentConfig?.status ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center py-1.5">
                                    <span className="text-main-subfont">Top Ads:</span>
                                    <span className="font-medium text-main-font text-xs">
                                        {topAdsOptions.find(o => o.value === currentConfig?.topAds)?.label}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center py-1.5">
                                    <span className="text-main-subfont">Bottom Ads:</span>
                                    <span className="font-medium text-main-font text-xs">
                                        {bottomAdsOptions.find(o => o.value === currentConfig?.bottomAds)?.label}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center pt-3 border-t border-main-border mt-2">
                                    <span className="text-main-subfont">Last Saved:</span>
                                    <span className="font-medium text-main-font text-xs">
                                        {formatDate(lastUpdated)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Full Configuration Table */}
                <div className="mt-6 bg-main-white rounded-2xl border border-main-border shadow-sm overflow-hidden">
                    <div className="p-5 md:p-6 border-b border-main-border">
                        <h2 className="text-lg font-bold text-main-font">All Configurations</h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-main-th border-b border-main-border">
                                    <th className="px-5 py-4 text-left text-sm font-semibold text-main-font">Type</th>
                                    <th className="px-5 py-4 text-center text-sm font-semibold text-main-font">Status</th>
                                    <th className="px-5 py-4 text-center text-sm font-semibold text-main-font">Top Ads</th>
                                    <th className="px-5 py-4 text-center text-sm font-semibold text-main-font">Bottom Ads</th>
                                    <th className="px-5 py-4 text-center text-sm font-semibold text-main-font">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {showboxTypes.map((type, index) => (
                                    <tr key={type.id} className={`border-b border-main-border hover:bg-main-bg transition-all ${index === showboxTypes.length - 1 ? 'border-b-0' : ''}`}>
                                        <td className="px-5 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className={`p-2.5 rounded-lg ${type.bgColor} border border-current/10`}>
                                                    <type.icon className={type.color} size={18} />
                                                </div>
                                                <span className="font-semibold text-main-font">{type.label}</span>
                                            </div>
                                        </td>
                                        <td className="px-5 py-4 text-center">
                                            <button
                                                onClick={() => handleStatusChange(type.id)}
                                                className={`relative w-12 h-6 rounded-full transition-all ${showboxData?.[`${type.id}_status`] ? 'bg-main-blue' : 'bg-main-border'}`}
                                            >
                                                <div className={`absolute top-1 w-4 h-4 bg-main-white rounded-full shadow transition-all ${showboxData?.[`${type.id}_status`] ? 'left-7' : 'left-1'}`} />
                                            </button>
                                        </td>
                                        <td className="px-5 py-4 text-center">
                                            <select
                                                value={showboxData?.[`${type.id}_top_Ads`] || 'OFF'}
                                                onChange={(e) => handleAdsChange(type.id, 'top', e.target.value)}
                                                className="px-3 py-2.5 bg-main-bg border border-main-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-main-blue/20 focus:border-main-blue text-main-font transition-all"
                                            >
                                                {topAdsOptions.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="px-5 py-4 text-center">
                                            <select
                                                value={showboxData?.[`${type.id}_bottom_Ads`] || 'OFF'}
                                                onChange={(e) => handleAdsChange(type.id, 'bottom', e.target.value)}
                                                className="px-3 py-2.5 bg-main-bg border border-main-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-main-blue/20 focus:border-main-blue text-main-font transition-all"
                                            >
                                                {bottomAdsOptions.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="px-5 py-4 text-center">
                                            <button
                                                onClick={() => setSelectedType(type.id)}
                                                className="px-4 py-2 text-sm text-main-blue hover:bg-main-th rounded-xl transition-all font-semibold border border-main-blue/20"
                                            >
                                                Preview
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShowBox;